/**
 * Order ID Persistence Utility
 *
 * Manages orderID persistence across page refreshes using localStorage + sessionStorage.
 *
 * Strategy:
 * - localStorage: Persists orderID across refresh (survives F5)
 * - sessionStorage: Detects browser close (flag clears when browser closes)
 * - 24-hour timeout: Clears stale orderIDs automatically
 */

const ORDER_ID_KEY = 'innolek_order_id';
const ORDER_TIMESTAMP_KEY = 'innolek_order_timestamp';
const BROWSER_SESSION_KEY = 'wizard_browser_session';
const STALE_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Initialize browser session detection
 *
 * Call this on app startup to:
 * 1. Detect if browser was closed (sessionStorage cleared)
 * 2. Clean up stale orderIDs from localStorage
 * 3. Set sessionStorage flag for future checks
 */
export function initializeOrderPersistence(): void {
  if (typeof window === 'undefined') return;

  const browserWasOpen = sessionStorage.getItem(BROWSER_SESSION_KEY);

  if (!browserWasOpen) {
    // Browser was closed → Clear localStorage orderID
    clearPersistedOrderId();
  } else {
    // Browser was just refreshed → Check for stale orderID
    const timestamp = localStorage.getItem(ORDER_TIMESTAMP_KEY);
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age > STALE_TIMEOUT) {
        clearPersistedOrderId();
      }
      // Order is still fresh, no action needed
    }
  }

  // Set flag for future checks
  sessionStorage.setItem(BROWSER_SESSION_KEY, 'true');
}

/**
 * Get persisted orderID from localStorage
 * Returns null if not found or stale
 */
export function getPersistedOrderId(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const orderId = localStorage.getItem(ORDER_ID_KEY);
    const timestamp = localStorage.getItem(ORDER_TIMESTAMP_KEY);

    if (!orderId || !timestamp) return null;

    // Check if stale
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > STALE_TIMEOUT) {
      clearPersistedOrderId();
      return null;
    }

    return orderId;
  } catch (_error) {
    return null;
  }
}

/**
 * Clear persisted orderID from localStorage
 */
export function clearPersistedOrderId(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(ORDER_ID_KEY);
    localStorage.removeItem(ORDER_TIMESTAMP_KEY);
  } catch (_error) {
    /* Silently ignore - expected behavior */
  }
}

/**
 * Persist orderID to localStorage
 */
export function persistOrderId(orderId: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(ORDER_ID_KEY, orderId);
    localStorage.setItem(ORDER_TIMESTAMP_KEY, Date.now().toString());
  } catch (_error) {
    /* Silently ignore */
  }
}

/**
 * Check if a valid persisted orderID exists
 */
export function hasPersistedOrderId(): boolean {
  return !!getPersistedOrderId();
}

/**
 * Get the age of the persisted orderID in milliseconds
 * Returns null if no timestamp exists
 */
export function getOrderIdAge(): number | null {
  if (typeof window === 'undefined') return null;

  try {
    const timestamp = localStorage.getItem(ORDER_TIMESTAMP_KEY);
    if (!timestamp) return null;
    return Date.now() - parseInt(timestamp, 10);
  } catch (_error) {
    return null;
  }
}
